import React from 'react';
import { BuyNewHomeMonthlyData } from '../types';

interface DataTableProps {
  data: BuyNewHomeMonthlyData[];
}

const formatCurrency = (value: number) => {
    if (Math.abs(value) < 0.01) value = 0;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumSignificantDigits: 3,
    }).format(value);
};

export const DataTable: React.FC<DataTableProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return null;
  }
  
  const yearlyDataMap = new Map<number, BuyNewHomeMonthlyData>();
  data.forEach(row => {
    if (row.month % 12 === 0 || row.month === data.length) {
      yearlyDataMap.set(row.year, row);
    }
  });

  if (!yearlyDataMap.has(data[0].year) && data.length < 12) {
      yearlyDataMap.set(data[0].year, data[data.length-1]);
  }

  const yearlyData = Array.from(yearlyDataMap.values()).sort((a, b) => a.year - b.year);

  const handleDownloadCsv = () => {
    const headers = [
      'Month', 'Date', 'Year', 'HomeValue',
      'Finance_MortgageBalance', 'Finance_HomeEquity', 'Finance_InvestedBalance', 'Finance_TaxSavings', 'Finance_NetWorth',
      'PayCash_MortgageBalance', 'PayCash_HomeEquity', 'PayCash_InvestedBalance', 'PayCash_TaxSavings', 'PayCash_NetWorth',
    ];

    const rows = data.map(d => [
      d.month, d.date, d.year,
      d.homeValue.toFixed(2),
      d.mortgageBalance.toFixed(2), d.homeEquity.toFixed(2), d.investedBalanceFinance.toFixed(2), d.taxSavingsThisYear.toFixed(2), d.financeScenarioNetWorth.toFixed(2),
      (0).toFixed(2), d.homeValue.toFixed(2), d.investedBalancePayCash.toFixed(2), (0).toFixed(2), d.payCashScenarioNetWorth.toFixed(2),
    ].join(','));

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "finance_vs_pay_cash_details.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div className="mt-8 bg-white dark:bg-slate-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 transition-colors">
      <div className="flex justify-between items-center mb-4 border-b dark:border-slate-600 pb-2">
        <h3 className="text-xl font-bold text-bp-slate dark:text-white">Year-by-Year Breakdown</h3>
        <button 
          onClick={handleDownloadCsv}
          className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-colors shadow"
        >
          Download Detailed Monthly CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 dark:text-gray-200 uppercase bg-gray-100 dark:bg-slate-700 sticky top-0">
            <tr>
              <th scope="col" rowSpan={2} className="px-2 py-3 align-middle text-center">Year</th>
              <th scope="col" colSpan={5} className="px-6 py-3 text-center border-l border-r border-gray-200 dark:border-slate-600 bg-blue-50 dark:bg-blue-900/40">Finance & Invest Scenario</th>
              <th scope="col" colSpan={5} className="px-6 py-3 text-center border-l border-r border-gray-200 dark:border-slate-600 bg-green-50 dark:bg-green-900/40">Pay Cash Scenario</th>
            </tr>
            <tr>
              {/* Finance Scenario Header */}
              <th scope="col" className="px-2 py-3 text-right bg-blue-50 dark:bg-blue-900/30 border-l border-gray-200 dark:border-slate-600">Balance</th>
              <th scope="col" className="px-2 py-3 text-right bg-blue-50 dark:bg-blue-900/30">Equity</th>
              <th scope="col" className="px-2 py-3 text-right bg-blue-50 dark:bg-blue-900/30">Investments</th>
              <th scope="col" className="px-2 py-3 text-right bg-blue-50 dark:bg-blue-900/30">Tax Savings</th>
              <th scope="col" className="px-2 py-3 font-semibold text-right bg-blue-50 dark:bg-blue-900/30 border-r border-gray-200 dark:border-slate-600">Net Worth</th>
              
              {/* Pay Cash Scenario Header */}
              <th scope="col" className="px-2 py-3 text-right bg-green-50 dark:bg-green-900/30 border-l border-gray-200 dark:border-slate-600">Balance</th>
              <th scope="col" className="px-2 py-3 text-right bg-green-50 dark:bg-green-900/30">Equity</th>
              <th scope="col" className="px-2 py-3 text-right bg-green-50 dark:bg-green-900/30">Investments</th>
              <th scope="col" className="px-2 py-3 text-right bg-green-50 dark:bg-green-900/30">Tax Savings</th>
              <th scope="col" className="px-2 py-3 font-semibold text-right bg-green-50 dark:bg-green-900/30 border-r border-gray-200 dark:border-slate-600">Net Worth</th>
            </tr>
          </thead>
          <tbody>
            {yearlyData.map((row) => (
              <tr key={row.year} className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700">
                <td className="px-2 py-4 font-medium text-gray-900 dark:text-white whitespace-nowrap text-center">{row.year}</td>
                {/* Finance Scenario Row */}
                <td className="px-2 py-4 text-right bg-blue-50/50 dark:bg-blue-900/10 border-l border-gray-200 dark:border-slate-600">{formatCurrency(row.mortgageBalance)}</td>
                <td className="px-2 py-4 text-right bg-blue-50/50 dark:bg-blue-900/10">{formatCurrency(row.homeEquity)}</td>
                <td className="px-2 py-4 text-right bg-blue-50/50 dark:bg-blue-900/10">{formatCurrency(row.investedBalanceFinance)}</td>
                <td className="px-2 py-4 text-right bg-blue-50/50 dark:bg-blue-900/10">{formatCurrency(row.taxSavingsThisYear)}</td>
                <td className="px-2 py-4 font-semibold text-right bg-blue-50/50 dark:bg-blue-900/10 border-r border-gray-200 dark:border-slate-600">{formatCurrency(row.financeScenarioNetWorth)}</td>
                
                {/* Pay Cash Scenario Row */}
                <td className="px-2 py-4 text-right bg-green-50/50 dark:bg-green-900/10 border-l border-gray-200 dark:border-slate-600">{formatCurrency(0)}</td>
                <td className="px-2 py-4 text-right bg-green-50/50 dark:bg-green-900/10">{formatCurrency(row.homeValue)}</td>
                <td className="px-2 py-4 text-right bg-green-50/50 dark:bg-green-900/10">{formatCurrency(row.investedBalancePayCash)}</td>
                <td className="px-2 py-4 text-right bg-green-50/50 dark:bg-green-900/10">{formatCurrency(0)}</td>
                <td className="px-2 py-4 font-semibold text-right text-green-700 dark:text-green-400 bg-green-50/50 dark:bg-green-900/10 border-r border-gray-200 dark:border-slate-600">{formatCurrency(row.payCashScenarioNetWorth)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};